import { EvaluationCalendarSummaryQuery } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useEffect, useMemo } from 'react';
import { useBoolean } from 'react-use';
import { useTrack } from 'utils/hooks/useMixPanel';

/** Minimum amount of timeslots available in the range to consider evaluation viable. If lower than this, force consult */
const MIN_AVAILABILITY_THRESHOLD = 1;

type Params = {
  /** Disable the forced redirect */
  disabled?: boolean;

  /** Number of available slots in the queried period */
  evaluationCalendarQuery: EvaluationCalendarSummaryQuery;
};

type Return = {
  /** Whether the redirect to consult has been triggered */
  isRedirecting?: boolean;

  /** Whether the availability is over the minimum threshold */
  isAvailabilityOverMinimum?: boolean;
};

export const useMinimumAvailability = ({ disabled = false, evaluationCalendarQuery }: Params): Return => {
  const [isRedirecting, setIsRedirecting] = useBoolean(false);
  const { onContinueToJotformRegistrationForm, isResumedLead, filters } = useSignupDirectCtx();
  const { track } = useTrack();

  const { data } = evaluationCalendarQuery;

  const isAvailabilityOverMinimum = useMemo(() => {
    if (!data) return undefined;

    return data.totalTimeSlots >= MIN_AVAILABILITY_THRESHOLD;
  }, [data]);

  useEffect(() => {
    if (disabled) return;
    if (!data) return;

    if (!isAvailabilityOverMinimum) {
      if (isResumedLead) {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.IGNORED_DIVERT, { isResumedLead, filters });
      } else {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.DIVERTED, { isResumedLead, filters });
        setIsRedirecting(true);
      }
    }
  }, [data, isAvailabilityOverMinimum, track, filters, onContinueToJotformRegistrationForm]);

  useEffect(() => {
    if (isRedirecting) {
      onContinueToJotformRegistrationForm();
    }
  }, [isRedirecting]);

  return { isAvailabilityOverMinimum, isRedirecting };
};
